/**
 * The three rehab fields of an analysis request, built in one place.
 *
 * The Auto/Light/Medium/Heavy presets were removed from the UI on a lender's
 * recommendation: a $/sqft band is not an investment-grade number, and the investor's own
 * budget is. That is a UI change, and this function is what keeps it from becoming an
 * engine change:
 *
 *   - `rehab_condition` is still sent. With the presets gone it stays 'auto' unless a
 *     saved analysis declared otherwise, so a BLANK budget still produces the
 *     auto-detected estimate — exactly what the default preset did. Reading blank as $0
 *     would make every deal look profitable, which is the opposite of the point.
 *
 *   - the amount is gated on the INPUT MODE, never on the condition label. The old gate
 *     was `rehabCondition === 'custom'`, which with the presets gone would have silently
 *     dropped a $/sqft budget the user had typed. PropertyConfirmation already had to undo
 *     that same defect for the total: a $100k budget carried in with condition 'auto' was
 *     dropped and became a ~$70k estimate.
 *
 * The backend precedence is custom_rehab_total > custom_rehab_per_sqft > condition, so a
 * typed budget outranks the label and always did. That is why removing the presets cannot
 * move a number for a deal whose budget was typed.
 */

/**
 * @param {object} a assumptions: { rehabCondition, customRehabInputMode,
 *                                  customRehabTotal, customRehabPerSqft }
 * @param {(v: any) => number|null} parseMoney parser for the formatted total
 *        (parseFormattedNumber in the app; injected so this stays pure and testable)
 */
export function rehabRequestFields(a, parseMoney) {
  const mode = a?.customRehabInputMode;
  const perSqft = parseFloat(a?.customRehabPerSqft);

  return {
    rehab_condition: a?.rehabCondition,
    custom_rehab_per_sqft:
      mode === 'persqft' && Number.isFinite(perSqft) && perSqft > 0 ? perSqft : null,
    // `!== 'persqft'` rather than `=== 'total'`: an older saved state can carry no mode at
    // all, and a total the user can see on screen must not be dropped for lack of a label.
    custom_rehab_total:
      mode !== 'persqft' && a?.customRehabTotal ? parseMoney(a.customRehabTotal) : null,
  };
}
